<template>
  <div class="space-y-4">
    <component
      :is="component"
      v-for="{ component, ...fieldProps } in customStepFieldsDefinition"
      :key="fieldProps.name"
      v-bind="fieldProps"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { storeToRefs } from "pinia";

import { checkoutBaseToDedicatedStep } from "@/helpers/checkout";
import { useCheckoutStore } from "@/stores/checkout";

import LfInput from "@/components/ui/inputs/LfInput.vue";
import LfTextarea from "@/components/ui/inputs/LfTextarea.vue";

import type { CheckoutCustomStep } from "@/models/checkout";

type CustomStepField = keyof Pick<
  CheckoutCustomStep,
  | "custom_title"
  | "custom_description"
  | "custom_button_text"
  | "custom_button_url"
>;

const { t } = useI18n();
const { selectedStep } = storeToRefs(useCheckoutStore());

const customStep = computed(() => {
  return checkoutBaseToDedicatedStep(selectedStep.value, "custom");
});

const handleFieldChange = (field: CustomStepField, value: string) => {
  if (!customStep.value) {
    return;
  }

  customStep.value[field] = value;
};

const customStepFieldsDefinition = computed(() => {
  if (!customStep.value) {
    return [];
  }

  const fields = [
    {
      component: LfInput,
      value: customStep.value.custom_title ?? "",
      name: `custom-step-${customStep.value.id}--title`,
      placeholder: t("COMMON.TITLE"),
      "data-cy": "step-custom-title-input",
      onKeyReleased: (event: string) =>
        handleFieldChange("custom_title", event),
    },
    {
      component: LfTextarea,
      value: customStep.value.custom_description ?? "",
      name: `custom-step-${customStep.value.id}--description`,
      placeholder: t("CHECKOUT.CUSTOM_STEP.DESCRIPTION_PLACEHOLDER"),
      rows: 3,
      onKeyReleased: (event: string) =>
        handleFieldChange("custom_description", event),
    },
    {
      component: LfInput,
      value: customStep.value.custom_button_text ?? "",
      name: `custom-step-${customStep.value.id}--button-text`,
      placeholder: t("CHECKOUT.CUSTOM_STEP.BUTTON_TEXT_PLACEHOLDER"),
      "data-cy": "step-custom-button-text-input",
      onKeyReleased: (event: string) =>
        handleFieldChange("custom_button_text", event),
    },
    {
      component: LfInput,
      value: customStep.value.custom_button_url ?? "",
      name: `custom-step-${customStep.value.id}--button-url`,
      placeholder: t("CHECKOUT.CUSTOM_STEP.BUTTON_URL_PLACEHOLDER"),
      "data-cy": "step-custom-button-url-input",
      onKeyReleased: (event: string) =>
        handleFieldChange("custom_button_url", event),
    },
  ];
  return fields;
});
</script>
